/*
 * ============================================================
 * SELL FLOW — assets/ccs-sell.js
 * ============================================================
 * What "Start Selling" does, and the two things that can interrupt it.
 *
 *   not signed in        -> sign-in modal, returning here with ?start=1
 *   signed in, no store  -> store registration modal, then straight on
 *   signed in, has store -> the design wizard opens
 *
 * The interruptions are steps in one flow, not dead ends: each one continues
 * into the wizard on success rather than dropping the seller back on the
 * marketing page to press the button again.
 */
(function (window, document) {
  'use strict';

  var root, api;
  var storeFormMounted = false;

  function $(selector) { return document.querySelector(selector); }

  // ── Modals ────────────────────────────────────────────────────────────────

  var lastFocused = null;

  function openModal(id) {
    var modal = $(id);
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    // Locking the page behind a modal stops the marketing copy scrolling under
    // it on mobile, which otherwise reads as the modal having been dismissed.
    document.body.classList.add('ccs-modal-open');
    var focusable = modal.querySelector('input, button, a[href]');
    if (focusable) focusable.focus();
  }

  function closeModal(id) {
    var modal = id ? $(id) : document.querySelector('.ccs-modal-root:not([hidden])');
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ccs-modal-open');
    // Returning focus matters for anyone on a keyboard: without it focus falls
    // back to the top of the document and they have to tab down again.
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function wireModals() {
    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-ccs="close-modal"]')) {
        event.preventDefault();
        closeModal();
      }
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeModal();
    });
  }

  // ── The wizard ────────────────────────────────────────────────────────────

  /*
   * Hands off to the shirt picker, the same questions-to-garment flow SIB uses.
   * Its 'design' mode verbs ("What are you designing?", "Design this") are the
   * right ones here.
   *
   * The picker resolves to a SIB style handle; the builder takes it from there.
   */
  function openWizard() {
    if (!window.ShirtPicker || !window.ShirtPicker.open) {
      // Should not happen -- the snippet is rendered by the page -- but failing
      // silently on the primary call to action would be the worst outcome.
      window.alert('The design tool could not be opened. Please reload the page.');
      return;
    }
    window.ShirtPicker.open({
      mode: 'design',
      onPick: function (product) { openBuilder(product); }
    });
  }

  // The builder is a page of its own, not something this page can open: it is
  // ~1MB of fabric and its own full-screen DOM, and putting it behind the
  // marketing copy would load all of that for every visitor who never clicks.
  // So the picker's choice travels there in the URL.
  function openBuilder(product) {
    var handle = (product && (product.handle || product.styleHandle)) || null;
    if (!handle) return;
    window.location.assign('/pages/design?style=' + encodeURIComponent(handle));
  }

  // ── The gate ──────────────────────────────────────────────────────────────

  function mountStoreForm() {
    var form = $('#ccs-modal-store-form');
    if (!form || storeFormMounted) return;
    storeFormMounted = true;

    window.CCSStoreForm.mount(form, {
      onSaved: function () {
        // Registration is a step on the way to designing, so finishing it
        // continues rather than congratulating.
        closeModal('#ccs-store-modal');
        root.dataset.hasStore = 'true';
        openWizard();
      },
      onCancel: function () { closeModal('#ccs-store-modal'); }
    });
  }

  /*
   * The server decides, not the page.
   *
   * data-logged-in and data-has-store come from Liquid and are used for the
   * instant path -- no round trip before the wizard opens for a seller who
   * already has a store. But the customer metafield can lag reality (a store
   * created while its metafield write failed), so anything that would send
   * somebody to the registration form is confirmed against /apps/ccs first.
   */
  function start() {
    if (root.dataset.loggedIn !== 'true') {
      openModal('#ccs-signin-modal');
      return;
    }

    if (root.dataset.hasStore === 'true') {
      openWizard();
      return;
    }

    setBusy(true);
    api.getStore().then(function (data) {
      setBusy(false);
      if (data.store) {
        root.dataset.hasStore = 'true';
        openWizard();
      } else {
        openModal('#ccs-store-modal');
        mountStoreForm();
      }
    }).catch(function () {
      // A failed check must not block the flow. Offering registration is the
      // recoverable wrong guess: a seller who already has a store is told so
      // by the server when they submit, rather than being stuck here.
      setBusy(false);
      openModal('#ccs-store-modal');
      mountStoreForm();
    });
  }

  function setBusy(busy) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-ccs="start"]'), function (button) {
      button.disabled = busy;
      button.classList.toggle('is-busy', busy);
    });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  function init() {
    root = $('#ccs-sell');
    if (!root) return;
    api = window.CCSApi;

    wireModals();

    // Every copy of the button on the page is the same button.
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-ccs="start"]');
      if (!button) return;
      event.preventDefault();
      start();
    });

    /*
     * ?start=1 fires the button on load. This is what closes the loop after
     * sign-in: the modal's return_to brings them back here with the flag, so
     * the round trip reads as one continuous action.
     *
     * The flag is then stripped from the URL so a refresh, or a bookmark, does
     * not re-open the wizard unbidden.
     */
    var params = new URLSearchParams(window.location.search);
    if (params.get('start') === '1') {
      params.delete('start');
      var query = params.toString();
      window.history.replaceState({}, '',
        window.location.pathname + (query ? '?' + query : ''));
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})(window, document);
